import { useEffect, useMemo, useState } from "react";
import { Layout, Menu, Select } from "antd";
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router";

import LogoIcon from "@/assets/icons/latest_vivo_logo.svg?react";
import VivoLogo from "@/assets/icons/vivo_logo.svg?react";
import HomeIcon from "@/assets/icons/home.svg?react";
import ButtonMenu from "@/components/UI/ButtonMenu";
import UserMenu from "@/components/UI/UserMenu";
import { useUser } from "@/contexts/UserContext";
import { usePathNavigation } from "@/hooks/usePathNavigation";
import { getFirstAvailableMenuPath } from "@/utils/landingPageUtils";

import { usePortfolioLandingPath } from "@/hooks/usePortfolioLandingPath";
import {
  getAdminLandingMenuItems,
  resolveAdminMenuKey,
  useAdminFeatureAvailability,
  useAdminMenuItems,
} from "@/hooks/useAdminNavigation";

const { Sider, Content } = Layout;

export default function AdminHome() {
  const location = useLocation();
  const navigate = useNavigate();
  const landingPath = usePortfolioLandingPath();
  const { userInfo } = useUser();
  const { hasAdminAccess } = useAdminFeatureAvailability();
  const [collapsed, setCollapsed] = useState(false);
  const adminMenuItems = useAdminMenuItems();
  const defaultLandingPage = getFirstAvailableMenuPath(
    getAdminLandingMenuItems(),
  );
  const { openMenu, newPathname } = usePathNavigation(
    location,
    defaultLandingPage,
  );
  const selectedKey =
    location.pathname === "/admin"
      ? defaultLandingPage
      : (resolveAdminMenuKey(location.pathname) ?? newPathname);
  const [openKeys, setOpenKeys] = useState([openMenu]);

  useEffect(() => {
    setOpenKeys([openMenu]);
  }, [openMenu]);

  const trialSelectorOptions = useMemo(() => {
    const regularTrials =
      userInfo?.trials?.map((trial) => ({
        label: `${trial.name ?? trial.id} | ${trial.id}`,
        value: trial.id,
      })) ?? [];

    const hasPortfolio = (userInfo?.trials?.length ?? 0) > 1;
    const portfolioOption = hasPortfolio
      ? [
          {
            label: "VIVO™ Clinical Portfolio",
            value: "PORTFOLIO",
          },
        ]
      : [];

    return [
      ...regularTrials,
      ...portfolioOption,
      {
        label: "VIVO™ Admin Settings",
        value: "admin-settings",
      },
    ];
  }, [userInfo?.trials]);

  const firstInitial = userInfo ? userInfo.first_name?.charAt(0) : "X";

  if (!hasAdminAccess) {
    return <Navigate to="/403" replace />;
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={256}
        collapsible
        trigger={null}
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        style={{ height: "100vh", position: "fixed" }}
        breakpoint="xl"
        collapsedWidth={80}
      >
        <div
          className={`flex flex-col h-full py-3 overflow-y-auto ${
            collapsed ? "px-5" : "px-6"
          }`}
        >
          <div>
            {collapsed ? (
              <div className="flex flex-col items-center mt-[24px] mb-3">
                <Link to="/dashboard">
                  <VivoLogo className="w-[32px] h-[18px]" />
                </Link>
              </div>
            ) : (
              <div className="flex flex-col mt-[24px] mb-3">
                <Link to="/dashboard">
                  <LogoIcon />
                </Link>
              </div>
            )}
          </div>

          {collapsed ? null : (
            <div className="mt-3">
              <Select
                data-testid="admin-navigation-select"
                className="w-full"
                options={trialSelectorOptions}
                value="admin-settings"
                onSelect={(value) => {
                  if (String(value) === "admin-settings") {
                    navigate("/admin");
                    return;
                  }

                  if (String(value) === "PORTFOLIO") {
                    navigate(landingPath);
                    return;
                  }

                  navigate(`/trial/${value}`);
                }}
              />
            </div>
          )}

          <div
            className={`flex-1 overflow-y-auto hide-scrollbar text-white ${
              collapsed ? "mt-2" : "mt-5"
            }`}
          >
            <Menu
              theme="dark"
              mode="inline"
              inlineIndent={3}
              defaultSelectedKeys={[]}
              className="admin-main-nav"
              classNames={{
                popup: {
                  root: "admin-main-nav-popup",
                },
              }}
              tooltip={{
                classNames: {
                  root: "admin-main-nav-tooltip",
                },
              }}
              items={adminMenuItems}
              openKeys={openKeys}
              selectedKeys={[selectedKey]}
              onOpenChange={(keys) => {
                setOpenKeys(keys);
              }}
            />
            <div className="flex flex-col">
              <Link to="/dashboard">
                {collapsed ? (
                  <div className="flex flex-col items-center text-sm mt-3 text-white hover:text-white">
                    <div className="border-t border-[#556C75] p-2">
                      <HomeIcon className="mt-1" />
                    </div>
                  </div>
                ) : (
                  <div className="flex text-sm px-1 mt-3 text-white hover:text-white">
                    <div className="flex gap-3 border-t border-[#556C75] p-2 w-full">
                      <ArrowLeftOutlined className="text-base" />
                      <p>My Trials</p>
                    </div>
                  </div>
                )}
              </Link>
            </div>
          </div>

          <div>
            {collapsed ? (
              <div className="flex flex-col items-center mb-4">
                <UserMenu mode="navigation">
                  <div className="bg-[#0E98D0] rounded-full h-7.5 w-7.5 flex justify-center items-center text-md border-none">
                    <span className="text-white">{firstInitial}</span>
                  </div>
                </UserMenu>
                <button
                  type="button"
                  onClick={() => setCollapsed(!collapsed)}
                  aria-label="Expand sidebar"
                  className="mt-5 h-10 w-10 shrink-0 flex items-center justify-center bg-[#ffffff1a] rounded-md text-white hover:bg-[#ffffff26] hover:text-white active:bg-[#ffffff33] active:scale-[0.98] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <DoubleRightOutlined className="text-inherit font-semibold" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-5 mb-4">
                <div className="flex-1">
                  <UserMenu mode="navigation">
                    <ButtonMenu userInfo={userInfo} navbar={true} />
                  </UserMenu>
                </div>
                <button
                  type="button"
                  onClick={() => setCollapsed(!collapsed)}
                  aria-label="Collapse sidebar"
                  className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#ffffff1a] rounded-[6px] text-white hover:bg-[#ffffff26] hover:text-white active:bg-[#ffffff33] active:scale-[0.98] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <DoubleLeftOutlined className="text-inherit font-semibold" />
                </button>
              </div>
            )}
          </div>
        </div>
      </Sider>
      <Layout
        className={`bg-[#EFF1F2] relative max-h-fit ${
          collapsed ? "ml-[80px]" : "ml-[256px]"
        }`}
      >
        <Content style={{ overflow: "initial", background: "#EFF1F2" }}>
          <Outlet context={{ sidebarCollapsed: collapsed }} />
        </Content>
      </Layout>
    </Layout>
  );
}
